<script setup lang="ts">
// The app's single parameterised button primitive: renders <button> or <RouterLink>
// with optional icon, responsive label, and tooltip (composed from Tooltip.vue).
// Variant/size presets keep the API small; call-site class overrides handle edge cases.
// See features/icon-action/SPEC.md for the full design rationale.
// DESIGN §7 names: primary = `accent`, secondary = `default`, quiet = `ghost`, small = `size="sm"` (PLAN §4, B2b).
import { computed, useAttrs } from 'vue'
import { twMerge } from 'tailwind-merge'
import { RouterLink, type RouteLocationRaw } from 'vue-router'
import Icon, { type IconName } from './Icon.vue'
import Tooltip from './Tooltip.vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    icon?: IconName
    label?: string
    tooltip?: string
    to?: RouteLocationRaw
    type?: 'button' | 'submit' | 'reset'
    variant?: 'accent' | 'default' | 'ghost' | 'danger'
    size?: 'sm' | 'md' | 'lg'
    disabled?: boolean
    busy?: boolean
    /** A long label that must read whole (ADR-113 D2: a duration naming an actor): it wraps instead
     *  of truncating, and the height becomes a minimum. */
    wrap?: boolean
  }>(),
  { type: 'button', variant: 'default', size: 'md', disabled: false, busy: false },
)

const attrs = useAttrs()

// Contract: an icon-only button (icon without label) MUST have a tooltip for
// accessibility. Warn in dev without breaking the render.
if (import.meta.env.DEV && props.icon && !props.label && !props.tooltip) {
  console.warn('[AppButton] icon without label requires `tooltip` for aria-label.')
}

const iconOnly = computed(() => !!props.icon && !props.label)

// The label is `hidden sm:inline` below 640 px, so an icon + label button would have no accessible
// name on a phone. Name it from the label there; a tooltip keeps its own aria-label (below).
const nameFromLabel = computed(() =>
  props.icon && props.label && !props.tooltip ? props.label : undefined,
)

const BASE =
  'inline-flex items-center justify-center rounded-control font-semibold transition-colors ' +
  'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring ' +
  'max-md:min-h-touch'

const VARIANT: Record<string, string> = {
  accent: 'bg-accent text-accent-contrast hover:bg-accent-hover',
  default: 'bg-surface-raised text-fg-primary border border-border-strong hover:border-fg-secondary',
  // Transparent, borderless icon button (close/dismiss). Normalizes the recurring
  // `bg-transparent` + `border-0` IconButton pattern (DraggableWindow, Modal, EquipmentCart).
  ghost: 'bg-transparent text-fg-secondary hover:bg-surface-raised hover:text-fg-primary',
  // Destructive = danger-soft tint (ADR-074).
  danger: 'bg-danger-soft text-danger border border-transparent hover:border-danger',
}

// Disabled = an inert raised tile with a readable muted label (F1, Q13): no opacity, no hover.
// Busy keeps the variant look (the call site's label says "Saving…"); aria-busy carries it.
const DISABLED: Record<string, string> = {
  accent: 'bg-surface-raised text-fg-secondary cursor-not-allowed',
  default: 'bg-surface-raised text-fg-secondary border border-border cursor-not-allowed',
  ghost: 'bg-transparent text-fg-secondary cursor-not-allowed',
  danger: 'bg-surface-raised text-fg-secondary border border-transparent cursor-not-allowed',
}

// Height is pinned (h-8/h-10/h-12) to match SIZE_ICON at the same size, so a
// labelled button and an icon-only button on the same row line up. Vertical
// padding is dropped in favour of the fixed height + flex centering.
const SIZE_LABEL: Record<string, string> = {
  sm: 'gap-2 h-8 px-3 text-xs',
  md: 'gap-2 h-10 px-4 text-sm',
  lg: 'gap-2 h-12 px-5 text-base',
}

// `wrap`: the same sizes as minimums, so a wrapped label grows the button instead of being clipped.
const SIZE_WRAP: Record<string, string> = {
  sm: 'gap-2 min-h-8 px-3 py-1 text-xs text-left',
  md: 'gap-2 min-h-10 px-4 py-2 text-sm text-left',
  lg: 'gap-2 min-h-12 px-5 py-2 text-base text-left',
}

const SIZE_ICON: Record<string, string> = {
  sm: 'h-8 w-8 max-md:min-w-touch',
  md: 'h-10 w-10 max-md:min-w-touch',
  lg: 'h-12 w-12 max-md:min-w-touch',
}

const classes = computed(() => [
  BASE,
  props.disabled ? DISABLED[props.variant] : VARIANT[props.variant],
  // A labelled ghost (a menu item) reads primary at rest, so its disabled muted label differs from
  // it (INC-149 (a)); an icon-only ghost stays muted at rest (DESIGN §7 "Icon button").
  props.variant === 'ghost' && !props.disabled && !iconOnly.value ? 'text-fg-primary' : '',
  props.busy && !props.disabled ? 'cursor-progress' : '',
  iconOnly.value ? SIZE_ICON[props.size] : (props.wrap ? SIZE_WRAP : SIZE_LABEL)[props.size],
])

const labelClass = computed(() => [
  props.wrap ? 'min-w-0 break-words' : 'min-w-0 truncate',
  props.icon ? 'hidden sm:inline' : '',
])

// `tailwind-merge` lets a call-site `class` override win deterministically over the
// variant/size presets (e.g. `h-5 w-5` beats `h-8`, `bg-danger/…` beats the variant `bg`).
// Without it, conflicting utilities are ambiguous by CSS source order.
const rootClass = computed(() => twMerge(classes.value, attrs.class as string))

// `class`/`style` are applied explicitly (merged / passthrough) — exclude them from the
// spread so `v-bind` does not re-append the raw `class` after `tailwind-merge` resolved it.
const rest = computed(() => {
  const spread = { ...attrs }
  delete spread.class
  delete spread.style
  return spread
})
</script>

<template>
  <Tooltip v-if="tooltip" :text="tooltip" interactive>
    <RouterLink
      v-if="to"
      :to="to"
      :aria-label="tooltip"
      :class="rootClass"
      :style="attrs.style"
      v-bind="rest"
    >
      <Icon v-if="icon" :name="icon" />
      <span v-if="label" :class="labelClass">{{ label }}</span>
    </RouterLink>
    <button
      v-else
      :type="type"
      :disabled="disabled || busy"
      :aria-busy="busy || undefined"
      :aria-label="tooltip"
      :class="rootClass"
      :style="attrs.style"
      v-bind="rest"
    >
      <Icon v-if="icon" :name="icon" />
      <span v-if="label" :class="labelClass">{{ label }}</span>
    </button>
  </Tooltip>

  <RouterLink v-else-if="to" :to="to" :aria-label="nameFromLabel" :class="rootClass" :style="attrs.style" v-bind="rest">
    <Icon v-if="icon" :name="icon" />
    <span v-if="label" :class="labelClass">{{ label }}</span>
  </RouterLink>
  <button
    v-else
    :type="type"
    :disabled="disabled || busy"
    :aria-busy="busy || undefined"
    :aria-label="nameFromLabel"
    :class="rootClass"
    :style="attrs.style"
    v-bind="rest"
  >
    <Icon v-if="icon" :name="icon" />
    <span v-if="label" :class="labelClass">{{ label }}</span>
  </button>
</template>
